import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Global banner for HTTP 429 (rate limit).
 *
 * The backend rate-limits two ways:
 *   - per organization, 1000 requests/hour (authenticated endpoints)
 *   - per IP, 5-20 requests/hour (public auth endpoints: register, login, password flows)
 *
 * Both used to fail silently: the request rejected, the page kept its loading/empty
 * state, and the user saw nothing. This banner subscribes to the `api-rate-limited`
 * event emitted by the axios interceptor and shows the reason plus the reset time.
 */

interface RateLimitDetail {
  /** Seconds until the limit resets, when the server sent a usable value. */
  retryAfterSeconds?: number;
  /** Endpoint path that was rejected, for context. */
  path?: string;
}

export default function RateLimitBanner() {
  const { t } = useTranslation('errors');
  const [detail, setDetail] = useState<RateLimitDetail | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<RateLimitDetail>;
      setDetail(custom.detail ?? {});
      // A new rejection re-arms the banner even if the previous one was dismissed.
      setDismissed(false);
    };
    window.addEventListener('api-rate-limited', handler);
    return () => window.removeEventListener('api-rate-limited', handler);
  }, []);

  if (!detail || dismissed) {
    return null;
  }

  const minutes = detail.retryAfterSeconds
    ? Math.max(1, Math.ceil(detail.retryAfterSeconds / 60))
    : null;

  return (
    <div
      id="rate-limit-banner"
      role="alert"
      className="bg-state-warn-bg-light dark:bg-state-warn-bg-dark border-b-2 border-state-warn-light dark:border-state-warn-dark"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-start sm:items-center justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 p-1.5 rounded-lg bg-state-warn-light/15 dark:bg-state-warn-dark/15">
              <AlertTriangle className="w-5 h-5 text-state-warn-light dark:text-state-warn-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-state-warn-light dark:text-state-warn-dark">
                {t('rateLimit.title', 'Too many requests')}
              </p>
              <p className="text-xs sm:text-sm mt-0.5 text-state-warn-light dark:text-state-warn-dark">
                {minutes
                  ? t('rateLimit.message', {
                      minutes,
                      defaultValue:
                        'The server is rate-limiting this account. Try again in about {{minutes}} minute(s).',
                    })
                  : t(
                      'rateLimit.messageNoEta',
                      'The server is rate-limiting this account. Try again shortly.'
                    )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t('rateLimit.dismiss', 'Dismiss')}
            className="flex-shrink-0 p-1.5 rounded-lg text-state-warn-light dark:text-state-warn-dark hover:bg-state-warn-light/15 dark:hover:bg-state-warn-dark/15 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
